import { fromFetch } from 'rxjs/fetch';
import { z } from 'zod/v4';

import { requiredHeaders } from '../../../models/required-headers';
import { unitsOfMeasurementOptions } from '../../../schemas/units-of-measurement.schema';
import { createRequestEnumPreprocessor } from '../../../utils/create-request-enum-preprocessor';
import { parseRequestSchema } from '../../../utils/parse-request-schema';
import { selectData } from '../../../utils/select-data';
import { withJwt } from '../../../utils/with-jwt';
import { routingProfileServiceEnvironment } from '../routing-profile-service-environment';
import { coPilotOptionsSchema } from '../schemas/copilot-options.schema';
import { costProfileSchema } from '../schemas/cost-profile.schema';
import { displayRestrictionTypeOptions } from '../schemas/display-restriction-type.schema';
import { ferriesOptions } from '../schemas/ferries.schema';
import { hazmatTypeOptions } from '../schemas/hazmat-type.schema';
import { routingOptionsSchema } from '../schemas/routing-options.schema';
import { routingProfileSchema } from '../schemas/routing-profile.schema';
import { routingTypeOptions } from '../schemas/routing-type.schema';
import { sideOfStreetAdherenceLevelOptions } from '../schemas/side-of-street-adherence-level.schema';
import { tollRoadTypeOptions } from '../schemas/toll-road-type.schema';
import { tunnelCategoryOptions } from '../schemas/tunnel-category.schema';
import { vehicleOptionsSchema } from '../schemas/vehicle-options.schema';

const requestSchema = routingProfileSchema
  .omit({
    routingProfileId: true,
  })
  .partial()
  .required({
    name: true,
    vehicleType: true,
    isAccountDefault: true,
  });

const schemaParser = routingProfileSchema.extend({
  routingProfileId: z.number().optional(),
  units: createRequestEnumPreprocessor(unitsOfMeasurementOptions).optional(),
  copilotOptions: coPilotOptionsSchema
    .extend({
      displayRestrictions: createRequestEnumPreprocessor(
        displayRestrictionTypeOptions
      ),
    })
    .optional(),
  routingOptions: routingOptionsSchema
    .extend({
      ferries: createRequestEnumPreprocessor(ferriesOptions),
      tollRoads: createRequestEnumPreprocessor(tollRoadTypeOptions),
      routeType: createRequestEnumPreprocessor(routingTypeOptions),
      sideOfStreetAdherenceLevel: createRequestEnumPreprocessor(
        sideOfStreetAdherenceLevelOptions
      ),
    })
    .optional(),
  vehicleOptions: vehicleOptionsSchema
    .extend({
      hazmatType: createRequestEnumPreprocessor(hazmatTypeOptions).optional(),
      tunnelCategory: createRequestEnumPreprocessor(
        tunnelCategoryOptions
      ).optional(),
    })
    .optional(),
  costProfiles: z
    .array(
      z.object({
        profile: costProfileSchema,
      })
    )
    .optional(),
});

/**
 * Create a new Routing Profile
 * @param request Routing Profile to create
 * @returns A Data object containing the newly created Routing Profile details
 */
export function postRoutingProfiles(
  this: unknown,
  request: z.infer<typeof requestSchema>
) {
  return withJwt((jwtAuthHeaders) =>
    fromFetch(
      new Request(
        routingProfileServiceEnvironment.resolve(this).routingProfilesUrl,
        {
          method: 'POST',
          headers: {
            ...requiredHeaders,
            ...jwtAuthHeaders,
          },
          body: parseRequestSchema(schemaParser, request),
        }
      )
    )
  ).pipe(
    selectData(
      z.object({
        message: z.string(),
        data: routingProfileSchema,
        warnings: z.array(z.string()),
      })
    )
  );
}
